export interface CompressImageOptions {
    maxDimension?: number;
    quality?: number;
}

const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_QUALITY = 0.8;
const OUTPUT_MIME_TYPE = 'image/jpeg';

interface Drawable {
    source: CanvasImageSource;
    width: number;
    height: number;
    release: () => void;
}

const loadWithImageBitmap = async (file: File): Promise<Drawable | null> => {
    if (typeof createImageBitmap !== 'function') {
        return null;
    }
    try {
        // EXIF orientation is not applied by canvas, so ask the decoder for it.
        // 'from-image' predates the bundled DOM typings for ImageBitmapOptions.
        const bitmap = await createImageBitmap(file, {
            imageOrientation: 'from-image',
        } as unknown as ImageBitmapOptions);
        return {
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            release: () => bitmap.close?.(),
        };
    } catch {
        return null;
    }
};

const loadWithImageElement = (file: File): Promise<Drawable | null> => {
    return new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            resolve({
                source: image,
                width: image.naturalWidth,
                height: image.naturalHeight,
                release: () => URL.revokeObjectURL(objectUrl),
            });
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
        };
        image.src = objectUrl;
    });
};

const toJpegBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> => {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), OUTPUT_MIME_TYPE, quality);
    });
};

const toJpegName = (filename: string): string => {
    const lastDot = filename.lastIndexOf('.');
    const stem = lastDot > 0 ? filename.slice(0, lastDot) : filename;
    return `${stem}.jpg`;
};

/**
 * Downscales and re-encodes an image as JPEG so uploads stay small.
 * Returns the original file whenever compression fails or does not help.
 */
export const compressImageFile = async (
    file: File,
    options: CompressImageOptions = {}
): Promise<File> => {
    const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
    const quality = options.quality ?? DEFAULT_QUALITY;

    if (!file.type.startsWith('image/')) {
        return file;
    }

    const drawable =
        (await loadWithImageBitmap(file)) ?? (await loadWithImageElement(file));

    if (!drawable || !drawable.width || !drawable.height) {
        return file;
    }

    try {
        const scale = Math.min(
            1,
            maxDimension / Math.max(drawable.width, drawable.height)
        );
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(drawable.width * scale));
        canvas.height = Math.max(1, Math.round(drawable.height * scale));

        const context = canvas.getContext('2d');
        if (!context) {
            return file;
        }

        // JPEG has no alpha channel; without this transparent areas turn black.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(drawable.source, 0, 0, canvas.width, canvas.height);

        const blob = await toJpegBlob(canvas, quality);
        if (!blob || blob.size >= file.size) {
            return file;
        }

        return new File([blob], toJpegName(file.name), {
            type: OUTPUT_MIME_TYPE,
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    } finally {
        drawable.release();
    }
};
